import { CSSProperties, forwardRef, memo, MouseEvent, Ref } from "react"

/* The scroll story: a sticky 3D "architect's model" on the right, chapters scrolling past on the left.
   home.js drives everything here through the data-* attributes (see page.js for what each one does),
   and writes into the counters, clocks and charts directly. --len is the number of chapters − 1.
   Beat timings (data-in / data-out) are in screens of scroll and match the ramps in home.js. */

interface LandingStoryProps {
  onHowItWorks: (event: MouseEvent<HTMLAnchorElement>) => void
}

const HEAT_LOSS_BARS = [
  { label: "Walls", value: 38 },
  { label: "Draughts", value: 18 },
  { label: "Roof", value: 17 },
  { label: "Windows", value: 15 },
  { label: "Floor", value: 12 }
]

const SIGNUP_HREF = "#early-access"

const LandingStory = forwardRef(
  ({ onHowItWorks }: LandingStoryProps, ref: Ref<HTMLElement>) => (
    <section
      className="story"
      id="story"
      ref={ref}
      style={{ "--len": 10 } as CSSProperties}
    >
      <div className="viewport">
        {/* the WebGL canvas is created per mount by LandingPage and prepended here */}
        <div className="wash" aria-hidden="true"></div>
        <div className="overlay" id="overlay">
          {/* 01 dimensions */}
          <span className="beat dimlabel" data-in=".5" data-out="1.5" data-dim="-2.9,0.05,5.6|2.96,0.05,5.6">5.8 m</span>
          <span className="beat dimlabel" data-in=".56" data-out="1.5" data-dim="3.7,0.05,4.0|3.7,0.05,-5.47">9.5 m</span>
          <span className="beat dimlabel" data-in=".62" data-out="1.5" data-dim="3.7,0,4.4|3.7,8.4,4.4">8.4 m</span>
          <div className="beat tag" data-in=".66" data-out="1.5" data-pin="1.2,7.6,-1.2" data-off="54,-40"><b>Hipped roof</b> 31° pitch</div>
          <div className="beat tag" data-in=".7" data-out="1.5" data-pin="-.6,4.6,4.75" data-off="-60,-24"><b>Front faces</b> NNE, 14°</div>

          {/* 02 heat loss in section */}
          <div className="beat tag hot" data-in="1.55" data-out="2.5" data-pin="-.4,7.6,-1.6" data-off="60,-44"><b>Roof</b> 0.35 W/m²K</div>
          <div className="beat tag hot" data-in="1.6" data-out="2.5" data-pin=".1,1.6,4.1" data-off="56,96"><b>Solid brick</b> 1.7 W/m²K</div>
          <div className="beat tag hot" data-in="1.65" data-out="2.5" data-pin=".1,4.2,-4.1" data-off="70,0"><b>Windows</b> 2.8 W/m²K</div>
          <div className="beat tag hot" data-in="1.7" data-out="2.5" data-pin=".1,.1,-1.5" data-off="70,36"><b>Timber floor</b> 0.70 W/m²K</div>

          {/* 03 overheating: plan cut, rooms by risk, then shading devices and cooling */}
          <div className="beat tag hot" data-in="2.6" data-out="3.55" data-pin="-1.14,3.0,-2.08" data-off="-70,-34"><b>Bedroom 2 · faces SSW</b> 11% of hours above 26 °C</div>
          <div className="beat tag ok" data-in="2.66" data-out="3.55" data-pin="-1.14,3.0,1.67" data-off="-70,-20"><b>Bedroom 1 · faces NNE</b> 1% of hours</div>
          <div className="beat clockface warm" data-in="2.55" data-out="3.55"><span id="heatClock">14:00</span><em>A hot summer day</em></div>
          <div className="beat tag" data-in="3.52" data-out="4.55" data-pin="-1.55,4.35,-4.2" data-off="-64,-40"><b>External blinds</b> SSW windows</div>
          <div className="beat tag" data-in="3.6" data-out="4.55" data-pin="-.06,2.62,-6.3" data-off="-60,40"><b>Brise soleil</b> over the kitchen doors</div>
          <div className="beat tag cool" data-in="3.68" data-out="4.55" data-pin="-2.53,4.2,-2.3" data-off="-50,-86"><b>Air-to-air heat pump</b> cools Bedroom 2</div>

          {/* 04 heat pump */}
          <div className="beat tag" data-in="4.6" data-out="5.5" data-pin="3.42,.55,-4.7" data-off="60,26"><b>Air source heat pump</b> 7 kW</div>
          <div className="beat tag" data-in="4.65" data-out="5.5" data-pin="2.2,1.5,-1.28" data-off="70,-40"><b>Hot water cylinder</b></div>
          <div className="beat tag" data-in="4.7" data-out="5.5" data-pin="-2.58,3.8,-2.3" data-off="-60,-40"><b>7 radiators</b> warmed at low flow temperature</div>

          {/* 05 solar */}
          <div className="beat tag sun" data-in="5.62" data-out="6.55" data-pin="0.2,7.4,-2.5" data-off="64,-50"><b>6 × 485 W</b> panels</div>
          <div className="beat tag" data-in="5.7" data-out="6.55" data-pin="3.1,1.2,-1.6" data-off="70,20"><b>9 kWh</b> battery</div>
          <div className="beat clockface" data-in="5.55" data-out="6.55"><span id="sunClock">06:00</span><em>21 June · sun study</em></div>

          {/* 08 portfolio stats */}
          <div className="beat stats" data-in="8.62" data-out="10.4">
            <div><strong id="homes">0</strong><span>homes analysed</span></div>
            <div><strong id="pipeline">£0</strong><span>upgrade pipeline*</span></div>
            <div><strong id="savings">£0</strong><span>bill savings a year*</span></div>
          </div>
          <p className="beat foot" data-in="8.62" data-out="10.4">*Illustrative projection: every home costed on the pilot home’s solar and battery plan. Rankings illustrative. Map data © OpenStreetMap contributors</p>
        </div>
      </div>

      <div className="chapters">
        <section className="chapter hero">
          <div className="inner">
            <p className="kicker">Retrofit investment intelligence</p>
            <h1>Make smarter retrofit investments.</h1>
            <p className="lede">Prioritise your investments with data-driven insights in seconds, not weeks.</p>
            <div className="cta-row">
              <a className="btn" href={SIGNUP_HREF}>Get early access</a>
              <a className="btn ghost" href="#c1" onClick={onHowItWorks}>See how it works</a>
            </div>
          </div>
        </section>

        <section className="chapter" id="c1">
          <div className="inner" data-in=".35" data-out="1.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker">Digital twin</p>
            <h2>An address in.<br />A digital twin out.</h2>
            <p className="lede">For most UK homes a street address is enough. Geometry, energy records and solar irradiance come from public datasets, automatically, with no site visit.</p>
            <div className="search">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L20 20" /></svg>
              <span className="typed" data-type="Chelsworth Drive, London SE18" data-tin=".42" data-tout=".82"></span>
            </div>
            <ul className="sources">
              <li><i></i>Ordnance Survey building geometry</li>
              <li><i></i>National EPC register</li>
              <li><i></i>Satellite data and local solar irradiance</li>
            </ul>
          </div>
        </section>

        <section className="chapter" id="c2">
          <div className="inner" data-in="1.35" data-out="2.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker hot">Heat loss</p>
            <h2>See exactly where the heat goes.</h2>
            <p className="lede">Heat loss modelled element by element, so every recommendation starts from the physics of the building.</p>
            <div className="bars" aria-label="Share of heat lost, illustrative">
              {HEAT_LOSS_BARS.map(({ label, value }) => (
                <div className="bar" key={label}>
                  <span>{label}</span>
                  <i data-v={value} style={{ "--v": value } as CSSProperties}></i>
                  <b>{value}%</b>
                </div>
              ))}
            </div>
            <p className="note">5.6 kW peak heat loss at −2 °C. Illustrative values.</p>
          </div>
        </section>

        <section className="chapter double" id="c3">
          <div className="inner" data-in="2.35" data-out="3.6" data-fade-in=".35" data-fade-out=".3">
            <p className="kicker hot">Overheating</p>
            <h2>Overheating risk, room by room.</h2>
            <p className="lede">Summers are getting hotter. Each room is modelled against the summer sun, so you can see which homes, and which rooms, need action.</p>
            <div className="ledger risk">
              <div><span>Bedroom 2 · faces SSW</span><b><i className="st bad" aria-hidden="true"></i>At risk · 11%</b></div>
              <div><span>Bathroom · faces SSW</span><b><i className="st warn" aria-hidden="true"></i>Watch · 7%</b></div>
              <div><span>Bedroom 1 · faces NNE</span><b><i className="st ok" aria-hidden="true"></i>Comfortable · 1%</b></div>
            </div>
            <p className="note">Share of occupied hours above 26 °C in a hot summer. Illustrative values.</p>
          </div>
          <div className="inner" data-in="3.4" data-out="4.65" data-fade-in=".3" data-fade-out=".35">
            <p className="kicker">Comfort upgrades</p>
            <h2>Shade first. Cool only where needed.</h2>
            <p className="lede">External blinds on the south-facing bedroom windows and a brise soleil over the kitchen doors keep the sun out. An air-to-air heat pump cools the one room that still needs it, and heats it in winter.</p>
            <div className="ledger">
              <div><span>Bedroom 2, hours above 26 °C</span><b>11%</b></div>
              <div><span>With external shading</span><b>4%</b></div>
              <div className="total"><span>With air-to-air cooling</span><b className="green">under 1%</b></div>
            </div>
            <p className="note">Comfort upgrades: no payback claimed. Illustrative values.</p>
          </div>
        </section>

        <section className="chapter" id="c4">
          <div className="inner" data-in="4.35" data-out="5.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker">Heat pump</p>
            <h2>Sized to the house.<br />Matched to funding.</h2>
            <p className="lede">Heat pump paths costed for each home, with funding such as the Boiler Upgrade Scheme flagged automatically.</p>
            <div className="ledger">
              <div><span>Installed cost</span><b>£12,500</b></div>
              <div><span>Boiler Upgrade Scheme grant</span><b className="green">−£7,500</b></div>
              <div className="total"><span>Net cost</span><b>£5,000</b></div>
              <div><span>Heating bill</span><b>£803 → £488 / yr</b></div>
              <div><span>Payback</span><b>16 yrs</b></div>
            </div>
            <p className="note">Illustrative. Runs through the battery on a heat-pump tariff.</p>
          </div>
        </section>

        <section className="chapter" id="c5">
          <div className="inner" data-in="5.35" data-out="6.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker sun">Solar + storage</p>
            <h2>Solar sized to the roof, and to the sun.</h2>
            <p className="lede">Panel layouts fitted to each roof face, with generation modelled for the home’s exact location and orientation.</p>
            <div className="figure">
              <strong><span data-count="2497" data-cin="5.55" data-cout="5.95">0</span> kWh</strong>
              <span>generated a year, 125% of the home’s electricity use</span>
            </div>
          </div>
        </section>

        <section className="chapter" id="c6">
          <div className="inner wide" data-in="6.35" data-out="7.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker">Smart tariffs</p>
            <h2>Returns, around the clock.</h2>
            <p className="lede">Battery and tariff strategy modelled across the day, with a smart tariff paying for surplus generation.</p>
            <figure className="daychart" id="daychart">
              <div className="legend-row">
                <span><i className="sw solar"></i>Solar generation</span>
                <span><i className="sw use"></i>Home use</span>
                <span><i className="sw batt"></i>Battery charge</span>
              </div>
              <svg viewBox="0 0 540 250" role="img" aria-label="A summer day: solar generation, home use and battery charge, with cheap overnight import and peak-time export"></svg>
              <div className="readout" aria-live="off">
                <strong id="dayTime">00:00</strong>
                <span id="dayMode">Running on the battery</span>
                <em id="dayBatt">Battery 30%</em>
              </div>
              <div className="tt" id="dayTip" hidden></div>
              <details className="table-view">
                <summary>View as a table</summary>
                <div className="tv-wrap">
                  <table id="dayTable">
                    <thead><tr><th>Time</th><th>Solar (kW)</th><th>Home use (kW)</th><th>Battery</th></tr></thead>
                    <tbody></tbody>
                  </table>
                </div>
              </details>
            </figure>
            <div className="tiles">
              <div><span>Electricity bill today</span><strong>£594</strong></div>
              <div><span>With solar, battery + smart tariff</span><strong>−£165</strong><em>a credit</em></div>
              <div className="green"><span>Saved a year</span><strong>£760</strong></div>
            </div>
            <p className="note">An illustrative summer day.</p>
          </div>
        </section>

        <section className="chapter" id="c7">
          <div className="inner wide" data-in="7.35" data-out="8.65" data-fade-in=".35" data-fade-out=".35">
            <p className="kicker">Returns</p>
            <h2>Returns you can underwrite.</h2>
            <p className="lede">Upgrade cost, annual savings, ROI and payback for every retrofit path. Not an energy rating: a decision-ready investment case.</p>
            <figure className="chart" id="chart">
              <svg viewBox="0 0 540 260" role="img" aria-label="Cumulative net position of the solar and battery upgrade over 25 years, paying back in 12.4 years"></svg>
              <figcaption>Cumulative net position, solar + battery (£9,410 installed, £760 a year). Illustrative.</figcaption>
            </figure>
            <div className="tiles">
              <div><span>ROI</span><strong>8.1%</strong></div>
              <div><span>Payback</span><strong>12.4 yrs</strong></div>
              <div className="green"><span>Home value</span><strong>+12.2%</strong><em>EPC D → B+</em></div>
            </div>
          </div>
        </section>

        <section className="chapter double portfolio" id="c8">
          <div className="inner" data-in="8.4" data-out="9.55" data-fade-in=".35" data-fade-out=".3">
            <p className="kicker">Portfolio</p>
            <h2>One home or 10,000.</h2>
            <p className="lede">Screen a single acquisition or triage an entire portfolio, with the same per-property precision and no marginal cost.</p>
          </div>
          <div className="inner" data-in="9.45" data-out="10.6" data-fade-in=".3" data-fade-out=".2">
            <h2>Focus retrofit where it delivers highest value.</h2>
            <p className="lede">Every home ranked by return, so capital goes where it works hardest.</p>
            <a className="btn" href={SIGNUP_HREF}>Get early access</a>
          </div>
        </section>
      </div>
    </section>
  )
)

/* Memoised: home.js writes into this markup directly, so it should never re-render. */
export default memo(LandingStory)
